/**
 * 토너먼트 모드 메시지 프로토콜.
 * 1:1 모드와 분리된 별도 채널 (`/parties/tournament/{TID}`) 에서 사용.
 *
 * 옵션 B 아키텍처: TournamentRoom 은 메타/브래킷만 관리. 매치 시작 시
 * 별도 Room DO 를 새로 띄우고 클라이언트가 그 Room 에 두 번째 WebSocket
 * 으로 직접 연결해 1:1 흐름을 그대로 재사용. 매치 종료는 Room DO 가
 * DO-to-DO 호출로 TournamentRoom 의 /match-result 에 보고.
 *
 * 흐름:
 *   t_hello → t_lobby* → (host) t_start_tournament → t_bracket
 *     → 양쪽 ready (t_match_ready_toggle) → 서버가 match Room DO 생성
 *     → 클라에 t_match_started(matchRoomId) → 클라가 그 Room 에 연결
 *     → 1:1 1매치 진행 (기존 protocol.ts ClientMsg/ServerMsg)
 *     → Room 종료 시 TournamentRoom 에 winner 보고
 *     → t_match_ended → 다음 매치 ready / 결승 → t_bracket(finished)
 */

// ======================================================================
//   공통 상수 / 상태
// ======================================================================

import { EndReason, PlayerStatsPublic } from "./protocol";

/** 토너먼트 인원 한계. 2~16. */
export const MIN_TOURNAMENT_PLAYERS = 2;
export const MAX_TOURNAMENT_PLAYERS = 16;

export type TPhase =
  | "tournament_lobby"
  | "tournament_running"
  | "tournament_finished";

export type ParticipantStatus =
  | "alive"
  | "eliminated"
  | "spectating"
  | "disconnected";

export type TMatchStatus =
  | "pending"
  | "ready"
  | "in_progress"
  | "finished"
  | "bye";

export type MatchPhase =
  | "coin_toss"
  | "pick_class"
  | "pick_boon"
  | "battle"
  | "ended";

// ======================================================================
//   공개 데이터 (브로드캐스트용)
// ======================================================================

export interface PublicParticipant {
  participantId: string;
  name: string;
  status: ParticipantStatus;
  isHost: boolean;
  online: boolean;
}

export interface PublicMatch {
  matchId: string;
  round: number;
  index: number;
  p1Id: string | null;
  p2Id: string | null;
  winnerId: string | null;
  status: TMatchStatus;
  readyP1: boolean;
  readyP2: boolean;
  /** 매치 진행 중 phase 표시 — Room 의 ended 메시지 시점에는 의미 없음. */
  matchPhase?: MatchPhase;
}

// ======================================================================
//   Client → Server (TournamentRoom 전용)
// ======================================================================

export type TClientMsg =
  | { type: "t_hello"; participantId: string; name: string }
  | { type: "t_leave_room" }
  | { type: "t_start_tournament" }
  | { type: "t_match_ready_toggle"; matchId: string }
  | { type: "t_after_match_choice"; choice: "spectate" | "leave" };

// ======================================================================
//   Server → Client (TournamentRoom 전용)
// ======================================================================

export type TServerMsg =
  | {
      type: "t_hello_ok";
      participantId: string;
      isHost: boolean;
      tournamentId: string;
    }
  | { type: "t_error"; message: string }
  | {
      type: "t_lobby";
      participants: PublicParticipant[];
      hostId: string | null;
      canStart: boolean;
    }
  | {
      type: "t_bracket";
      phase: TPhase;
      bracket: PublicMatch[][];
      participants: PublicParticipant[];
      champion: string | null;
      myCurrentMatchId: string | null;
    }
  /** 매치 시작 알림 — 양쪽 모두 ready 누르면 발송.
   *  matchRoomId 로 클라가 별도 Room DO (1:1) 에 직접 연결해야 함. */
  | {
      type: "t_match_started";
      matchId: string;
      matchRoomId: string;
      firstPickId: string;
    }
  /** 매치 종료 — Room DO 가 결과를 TournamentRoom 에 보고하면 broadcast. */
  | {
      type: "t_match_ended";
      matchId: string;
      winnerId: string | null;
      reason: EndReason;
      p1Stats: PlayerStatsPublic;
      p2Stats: PlayerStatsPublic;
      youWon: boolean | null;
    };

// ======================================================================
//   DO 내부 RPC (TournamentRoom ↔ Room)
// ======================================================================

/** TournamentRoom → Room: 매치 시작 시 컨텍스트 init. */
export interface MatchInitPayload {
  tournamentId: string;
  matchId: string;
  p1Id: string;
  p2Id: string;
  p1Name: string;
  p2Name: string;
}

/** Room → TournamentRoom: 매치 종료 결과 보고. */
export interface MatchResultPayload {
  matchId: string;
  winnerId: string | null;
  reason: EndReason;
  p1Stats: PlayerStatsPublic;
  p2Stats: PlayerStatsPublic;
}
